import i18next from 'i18next';
import i18n from 'i18next';
import jsPDF from 'jspdf';

import { TicketType } from 'store/ticket-service/types';

import { normalizeObjectKeys } from 'utils/normalizeObjectKeys';
import { orderDiagrams } from 'utils/orderDiagrams';

import { NamespacesList, TicketStages } from 'core/enums/common';
import { errorNotify } from 'core/helpers/toasts';

import { ITestCase, ITestPlan } from 'components/TicketSteps/TestCasesStep/types';

const pdfTitleSize = 24;
const pdfSubtitleSize = 16;
const pdfFontSize = 12;

const lineHeightMultiplier = 0.55;
const lineSeparator = pdfFontSize * lineHeightMultiplier;

const millimeterToPixelRatio = 3.78;

const pdfPageMargin = 10;
const pdfPageWidth = 210;
const pdfPageHeight = 297;

const translate = (translaionPath: string) => {
	return i18next.t(translaionPath);
};

const reverseString = (str: string) => {
	return str.split('').reverse().join('');
};

const fetchArslanWessamAFont = async (): Promise<Blob | null> => {
	try {
		const response = await fetch(
			'https://ellogytemplatestorge.blob.core.windows.net/fonts/Arslan-Wessam.ttf'
		);

		return response.blob();
	} catch (e) {
		errorNotify(
			i18n.t('common:errors.arabicFontLoadTitle'),
			i18n.t('common:errors.arabicFontLoadDescription')
		);

		return null;
	}
};

const blobToBase64 = (blob: Blob): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			const base64data = reader.result as string;
			resolve(base64data.split(',')[1]);
		};
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(blob);
	});
};

const stringHasOnlyNumbers = (string: string) => {
	// If string contains only numbers, returns false
	const regex = new RegExp(/^\d+$/);

	return regex.test(string);
};

const cleanScenariosNumbering = (array: Array<string>) =>
	array.filter((value) => value && value.length > 1 && !stringHasOnlyNumbers(value));

export const GeneratePdfFromTicket = async (ticketData: TicketType, ticketStep: TicketStages) => {
	const storage = localStorage.getItem('i18nextLng');

	const pdf = new jsPDF();

	let currentPdfY = pdfPageMargin;

	const contentDirection = storage && storage === 'ar' ? 'rtl' : 'ltr';
	const pdfFontFamily = storage === 'ar' ? 'arslamWessamA' : 'lato';

	if (storage === 'ar') {
		const arslanWessamAFont = await fetchArslanWessamAFont();

		if (arslanWessamAFont) {
			const parseFont = await blobToBase64(arslanWessamAFont);

			pdf.addFileToVFS('Arslan Wessam A-normal.ttf', parseFont);
			pdf.addFont('Arslan Wessam A-normal.ttf', 'arslamWessamA', 'normal');

			pdf.addFileToVFS('Arslan Wessam A-bold.ttf', parseFont);
			pdf.addFont('Arslan Wessam A-bold.ttf', 'arslamWessamA', 'bold');
		}
	}

	const checkPdfPageEnd = (fontSize = pdfFontSize) => {
		if (currentPdfY + fontSize * lineHeightMultiplier > pdfPageHeight - pdfPageMargin) {
			pdf.addPage([pdfPageWidth, pdfPageHeight]);
			currentPdfY = pdfPageMargin;
		}
	};

	const splitTextToWidth = (text: string): Array<string> => {
		const words = text.split(' ');
		let currentLine = '';
		const lines = [];

		words.forEach((word) => {
			const currentWidth = pdf.getTextWidth(currentLine + ' ' + word);

			if (currentWidth <= pdfPageWidth - pdfPageMargin * 2) {
				currentLine += currentLine === '' ? word : ` ${word}`;
			} else {
				lines.push(currentLine);
				currentLine = word;
			}
		});

		if (currentLine !== '') lines.push(currentLine);

		return lines;
	};

	const splitTextToWidthRtl = (text: string, fontSize: number) => {
		const maxWidth = pdfPageWidth - 2 * pdfPageMargin;

		const lines = pdf.splitTextToSize(text, maxWidth);

		lines.forEach((line: string) => {
			checkPdfPageEnd(fontSize);

			const reversedLine = reverseString(line);

			const textWidth =
				(pdf.getStringUnitWidth(reversedLine) * fontSize) / pdf.internal.scaleFactor;

			const lineX = pdfPageMargin + maxWidth - textWidth;

			pdf.text(reversedLine, lineX, currentPdfY);

			currentPdfY += lineSeparator;
		});
	};

	const pdfAddLine = (line: string, fontSize = pdfFontSize) => {
		if (contentDirection === 'rtl') {
			splitTextToWidthRtl(line, fontSize);
		} else {
			splitTextToWidth(line).forEach((subLine: string) => {
				checkPdfPageEnd(fontSize);

				pdf.text(subLine, pdfPageMargin, currentPdfY);

				currentPdfY += lineSeparator;
			});
		}
	};

	const titleToPdf = (title: string, fontSize = pdfSubtitleSize) => {
		checkPdfPageEnd(fontSize);

		pdf.setFontSize(fontSize);
		pdf.setFont(pdfFontFamily, 'normal', 700);

		pdfAddLine(title, fontSize);

		pdf.setFont(pdfFontFamily, 'normal', 400);
		pdf.setFontSize(pdfFontSize);

		currentPdfY += fontSize * lineHeightMultiplier * 0.5;
	};

	const descriptionToPdf = () => {
		titleToPdf(translate(`${NamespacesList['reportStep']}:titles.description`), pdfSubtitleSize);

		checkPdfPageEnd();

		pdfAddLine(ticketData.description);

		currentPdfY += 4;
	};

	const userStoriesToPdf = () => {
		titleToPdf(translate(`${NamespacesList['reportStep']}:titles.userStories`), pdfSubtitleSize);

		ticketData.ticketSummaries?.forEach((story, index) => {
			pdfAddLine(`${index + 1}. ${story.data}`);

			currentPdfY += 2;

			titleToPdf(translate(`${NamespacesList['generalStep']}:commonText.scenarios`), pdfFontSize);

			story?.summaryScenarios?.forEach((scenario, index) => {
				pdfAddLine(`${index + 1}. ${scenario.title}`);

				pdfAddLine(scenario.description);

				currentPdfY += 2;
			});

			currentPdfY += 2;

			titleToPdf(
				translate(`${NamespacesList['generalStep']}:commonText.acceptanceCriteria`),
				pdfFontSize
			);

			story?.summaryAcceptanceCriteria?.forEach((acceptanceCriteria, index) => {
				pdfAddLine(`${index + 1}. ${acceptanceCriteria.title}`);

				pdfAddLine(acceptanceCriteria.description);

				currentPdfY += 1;
			});
		});

		currentPdfY += 4;
	};

	const parseTableValue = (
		value: string | Array<string> | Record<string, any>,
		index = 0
	): string | Array<string> | Record<string, any> => {
		if (typeof value === 'string') return [`${index ? `${index}.${value}` : value}`];

		if (Array.isArray(value))
			return value.map((deepValue, index) => parseTableValue(deepValue, index + 1));

		if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
			return Object.entries(value).map(([key, deepValue]) => [
				parseTableValue(
					`${translate(`${NamespacesList['useCasesStep']}:descriptionTable.${key}`)}:`
				),
				parseTableValue(deepValue),
			]);
		}

		return value;
	};

	const parseTableToPdf = (tableObj: Record<string, any>) => {
		return Object.entries(tableObj).map(([key, value]) => [
			key,
			Array(parseTableValue(value)).flat(6),
		]);
	};

	const addPdfImage = async (imageUrl: string): Promise<Record<string, any> | null> => {
		const response = await fetch(imageUrl);

		if (response.ok) {
			const imageBlob = await response.blob();
			const img = new Image();

			return new Promise((resolve) => {
				img.onload = () => {
					const width =
						img.width / millimeterToPixelRatio > pdfPageWidth - 2 * pdfPageMargin
							? pdfPageWidth - 2 * pdfPageMargin
							: img.width / millimeterToPixelRatio;
					const height =
						img.height / millimeterToPixelRatio > pdfPageHeight - currentPdfY - 2 * pdfPageMargin
							? pdfPageHeight - currentPdfY - 2 * pdfPageMargin
							: img.height / millimeterToPixelRatio;
					const marginLeft =
						width >= pdfPageWidth - 2 * pdfPageMargin
							? pdfPageMargin
							: (pdfPageWidth - 2 * pdfPageMargin - width) / 2;

					resolve({
						img,
						format: 'PNG',
						marginX: marginLeft,
						marginY: currentPdfY,
						width,
						height,
						blob: imageBlob,
					});
				};

				img.src = URL.createObjectURL(imageBlob);
			});
		} else {
			return new Promise((resolve) => resolve(null));
		}
	};

	const useCasesToPdf = async () => {
		titleToPdf(translate(`${NamespacesList['reportStep']}:titles.useCases`), pdfSubtitleSize);

		if (ticketData?.useCases) {
			for (const [index, usecase] of ticketData.useCases.entries()) {
				titleToPdf(
					`${translate(`${NamespacesList['useCasesStep']}:generalText.useCase`)} #${index + 1}: ${
						usecase.title
					}`
				);

				const parsedTable = parseTableToPdf(JSON.parse(usecase.tables[0].table));

				parsedTable.forEach((row) => {
					if (typeof row[0] === 'string')
						titleToPdf(
							translate(`${NamespacesList['useCasesStep']}:descriptionTable.${row[0]}`),
							pdfFontSize
						);

					if (Array.isArray(row[1]))
						row[1].forEach((point) => {
							if (typeof point === 'string') {
								const fontWeight = point === 'Role:' || point === 'Responsibilities:' ? 700 : 400;
								if (point.endsWith(':')) currentPdfY += 2;
								pdf.setFont(pdfFontFamily, 'normal', fontWeight);
								pdfAddLine(point);
							}
						});

					currentPdfY += 2;
				});

				pdf.addPage([pdfPageWidth, pdfPageHeight]);
				currentPdfY = pdfPageMargin;

				if (orderDiagrams(usecase.diagrams).filter((diagram) => diagram.pictureLink).length) {
					for (const diagram of orderDiagrams(usecase.diagrams).filter(
						(diagram) => diagram.pictureLink
					)) {
						titleToPdf(
							translate(`${NamespacesList['useCasesStep']}:diagrams.${diagram.title}`),
							pdfFontSize
						);

						const imageData = await addPdfImage(diagram.pictureLink.replace('svg', 'png'));

						if (imageData !== null) {
							const { img, type, marginX, marginY, width, height, blob } = imageData;

							pdf.addImage(img, type, marginX, marginY, width, height);

							URL.revokeObjectURL(URL.createObjectURL(blob));

							pdf.addPage([pdfPageWidth, pdfPageHeight]);
							currentPdfY = pdfPageMargin;
						}
					}
				}
			}
		}
	};

	const testPlanToPdf = (testPlan: ITestPlan) => {
		titleToPdf(translate(`${NamespacesList['testCasesStep']}:testPlan.title`), pdfFontSize);

		Object.entries(testPlan).forEach(([key, value]) => {
			const parsedValue = Array.isArray(value) ? value : value.split('\n');

			if (key !== 'id' && parsedValue?.length) {
				if (Array.isArray(parsedValue)) {
					titleToPdf(
						`${translate(`${NamespacesList['testCasesStep']}:testPlan.${key}`)}: `,
						pdfFontSize
					);
					parsedValue.forEach((item, index) => {
						pdfAddLine(`${index + 1}.${item}`);
					});
				} else {
					titleToPdf(
						`${translate(`${NamespacesList['testCasesStep']}:testPlan.${key}`)}: `,
						pdfFontSize
					);
					pdfAddLine(parsedValue);
				}
			}
		});

		currentPdfY += 3;
	};

	const testScenarioToPdf = (testScenarios: Array<string>) => {
		titleToPdf(translate(`${NamespacesList['testCasesStep']}:testScenarios.title`), pdfFontSize);

		testScenarios.forEach((scenario, index) => {
			pdfAddLine(`${index + 1}.${scenario}`);
		});
	};

	const testCasesToPdf = (testCases: Array<ITestCase>) => {
		titleToPdf(translate(`${NamespacesList['testCasesStep']}:testCases.title`), pdfFontSize);

		testCases.forEach((testCase: ITestCase) => {
			Object.entries(testCase).forEach(([key, value]) => {
				const parsedValue = Array.isArray(value) ? value : value.split('\n');

				if (key !== 'id') {
					if (Array.isArray(parsedValue)) {
						titleToPdf(
							`${translate(`${NamespacesList['testCasesStep']}:testCases.${key}`)}: `,
							pdfFontSize
						);
						parsedValue.forEach((item, index) => {
							pdfAddLine(`${index + 1}.${item}`);
						});
					} else {
						titleToPdf(
							`${translate(`${NamespacesList['testCasesStep']}:testCases.${key}`)}: `,
							pdfFontSize
						);
						pdfAddLine(parsedValue);
					}

					currentPdfY += 2;
				}
			});

			currentPdfY += 2;
		});
	};

	const testsToPdf = () => {
		titleToPdf(translate(`${NamespacesList['reportStep']}:titles.testCases`), pdfSubtitleSize);

		const cleanedTests = ticketData.userStoryTests
			? ticketData.userStoryTests.map((test) => ({
					...test,
					testScenarios: cleanScenariosNumbering(test.testScenarios),
					useCaseTitle: normalizeObjectKeys(test)['usecasetitle'],
			  }))
			: [];

		cleanedTests.forEach((userStoryTest, index) => {
			titleToPdf(`${index + 1}.${normalizeObjectKeys(userStoryTest)['usecasetitle']}`, pdfFontSize);

			currentPdfY += 2;

			testPlanToPdf(userStoryTest.testPlan);

			currentPdfY += 3;

			testScenarioToPdf(userStoryTest.testScenarios);

			currentPdfY += 3;

			testCasesToPdf(userStoryTest.testCases);
		});

		currentPdfY += 4;
	};

	const NotificationsToPdf = () => {
		titleToPdf(translate(`${NamespacesList['reportStep']}:titles.notifications`), pdfSubtitleSize);

		ticketData.notifications.forEach((notification) => {
			pdfAddLine(notification.title);

			pdfAddLine(notification.description);

			pdfAddLine(
				`${translate(`${NamespacesList['notificationsStep']}:notifications.Email`)} - ${String(
					notification.email
				)}`
			);
			pdfAddLine(
				`${translate(`${NamespacesList['notificationsStep']}:notifications.Push`)} - ${String(
					notification.push
				)}`
			);
			pdfAddLine(
				`${translate(`${NamespacesList['notificationsStep']}:notifications.SMS`)} - ${String(
					notification.sms
				)}`
			);

			currentPdfY += 4;
		});
	};

	const businessImpactToPdf = () => {
		if (ticketData?.businessImpact?.length) {
			titleToPdf(
				translate(`${NamespacesList['reportStep']}:titles.businessImpact`),
				pdfSubtitleSize
			);

			const businessImpact: Array<string> = JSON.parse(ticketData.businessImpact);

			businessImpact.forEach((impactItem) => {
				pdfAddLine(impactItem);
			});

			currentPdfY += 4;
		}
	};

	pdf.setFont(pdfFontFamily, 'normal', 400);
	pdf.setFontSize(pdfFontSize);

	titleToPdf(ticketData.title, pdfTitleSize);

	if (ticketStep === TicketStages.general || ticketStep === TicketStages.report) {
		descriptionToPdf();
		userStoriesToPdf();
	}

	if (ticketStep === TicketStages.useCases || ticketStep === TicketStages.report)
		await useCasesToPdf();

	if (ticketStep === TicketStages.testCases || ticketStep === TicketStages.report) testsToPdf();

	if (ticketStep === TicketStages.notifications || ticketStep === TicketStages.report)
		NotificationsToPdf();

	if (ticketStep === TicketStages.report) businessImpactToPdf();

	return pdf;
};
